/**
 * Reconciliation Hub Page (UI-10)
 *
 * Account status cards at top + history table below.
 * "Reconcile" button per card ("Resume" when a session is open), "Start New" in header.
 * On BE-13: the hub lists active accounts once the ledger has started; a history row opens its session.
 * Route: /accounting/reconciliation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Card, CardContent, Grid,
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, StatusChip, CurrencyText } from '../../components/accounting';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { serverMessage } from '../../utils/apiConvert';
import { reconciliationService } from '../../services/accounting/reconciliation.service';

const SESSION_STATUS = {
  IN_PROGRESS: { label: 'In progress', color: 'warning.main' },
  COMPLETED: { label: 'Completed', color: 'success.main' },
  CANCELLED: { label: 'Cancelled', color: 'text.secondary' },
};

const historyColumns = (names) => [
  { key: 'statementDate', label: 'Date', width: '14%', render: (r) => formatDate(r.statementDate) },
  { key: 'accountName', label: 'Account', width: '20%' },
  { key: 'statementBalance', label: 'Statement Bal.', width: '16%', align: 'right', render: (r) => <CurrencyText amount={r.statementBalance} /> },
  { key: 'systemBalance', label: 'System Bal.', width: '16%', align: 'right', render: (r) => (r.systemBalance == null ? '—' : <CurrencyText amount={r.systemBalance} />) },
  { key: 'adjustmentTotal', label: 'Adjustments', width: '12%', align: 'right', render: (r) => (Number(r.adjustmentTotal || 0) === 0 ? '—' : <CurrencyText amount={r.adjustmentTotal} />) },
  { key: 'reconciledBy', label: 'Reconciled By', width: '12%', render: (r) => (r.reconciledBy ? names[r.reconciledBy] || '—' : '—') },
  {
    key: 'status', label: 'Status', width: '10%',
    render: (r) => (
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium, color: SESSION_STATUS[r.status]?.color }}>
        {SESSION_STATUS[r.status]?.label || r.status}
      </Typography>
    ),
  },
];

const ACCT_TYPE_LABELS = {
  BANK: 'Bank',
  CASH: 'Cash',
  CREDIT_CARD: 'Credit Card',
  WALLET: 'Digital Wallet',
};

const Reconciliation = ({ currentUser }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acctRes, histRes] = await Promise.all([
        reconciliationService.getAccountStatus(),
        reconciliationService.getHistory(),
      ]);
      setAccounts(acctRes?.data || []);
      setHistory(histRes?.data || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load reconciliation data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
  useEffect(() => {
    reconciliationService.getUserNames(typingCenterId).then(setNames).catch(() => {});
  }, [typingCenterId]);

  // An account with a session in progress resumes it; it cannot start a second one
  const handleReconcile = (account) => {
    if (account.openReconciliationId) navigate(`/accounting/reconciliation/${account.openReconciliationId}`);
    else navigate('/accounting/reconciliation/new', { state: { account } });
  };

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1 }}>
        Reconciliation
      </Typography>

      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrow />}
          onClick={() => navigate('/accounting/reconciliation/new')}
          disabled={!loading && accounts.length === 0}
          sx={{ textTransform: 'none', fontSize: fs.sm }}
        >
          Start New
        </Button>
      </Box>

      {/* Account Status Cards */}
      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>
        Account Status
      </Typography>
      {!loading && accounts.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Nothing to reconcile yet — accounts appear here once the typing center's ledger has started.
        </Alert>
      )}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {accounts.map((acct) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={`${acct.accountType}-${acct.accountId}`}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
                height: '100%',
              }}
              onClick={() => handleReconcile(acct)}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, lineHeight: 1.3 }}>
                    {acct.accountName}
                  </Typography>
                  <StatusChip type="reconciliationStatus" value={acct.status} />
                </Box>
                <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 0.5 }}>
                  {ACCT_TYPE_LABELS[acct.accountType] || acct.accountType}
                </Typography>
                <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>
                  {formatCurrency(acct.systemBalance)}
                </Typography>
                <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>
                  {acct.lastReconciled
                    ? `Last: ${formatDate(acct.lastReconciled)}`
                    : 'Never reconciled'}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  color={acct.openReconciliationId ? 'warning' : 'primary'}
                  onClick={(e) => { e.stopPropagation(); handleReconcile(acct); }}
                  sx={{ mt: 1, textTransform: 'none', fontSize: fs.xs }}
                >
                  {acct.openReconciliationId ? 'Resume' : 'Reconcile'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* History */}
      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>
        History
      </Typography>
      <DataTable
        columns={historyColumns(names)}
        data={history}
        loading={loading}
        onRowClick={(row) => navigate(`/accounting/reconciliation/${row.id}`)}
        emptyMessage="No reconciliations completed yet."
      />
    </Box>
  );
};

export default Reconciliation;
